import { dialog } from '../../../store/dialogStore';
import { foldText } from '../../../utils/fold';
import { useEditor, EditorContent, ReactRenderer, ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import tippy from 'tippy.js';
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Link2, AtSign } from 'lucide-react';

const fileToBase64 = (file) => new Promise((res) => {
  const r = new FileReader();
  r.onload = (e) => res(e.target.result);
  r.readAsDataURL(file);
});

function ResizableImageView({ node, updateAttributes, selected }) {
  const imgRef = useRef(null);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = node.attrs.width || imgRef.current?.offsetWidth || 300;

    const onMouseMove = (mv) => {
      const newWidth = Math.max(80, Math.round(startWidth + (mv.clientX - startX)));
      updateAttributes({ width: newWidth });
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [node.attrs.width, updateAttributes]);

  return (
    <NodeViewWrapper as="div" className="relative inline-block group my-1 max-w-full" onMouseDown={(e) => e.preventDefault()}>
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        style={node.attrs.width ? { width: node.attrs.width + 'px', maxWidth: '100%' } : { maxWidth: '100%' }}
        className={`rounded border-2 block transition-colors ${selected ? 'border-blue-400' : 'border-white/30'}`}
      />
      <div
        onMouseDown={onResizeMouseDown}
        className="absolute bottom-1 right-1 w-5 h-5 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-sm flex items-center justify-center"
        title="Redimensionar"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="white">
          <circle cx="8.5" cy="8.5" r="1.2"/>
          <circle cx="5" cy="8.5" r="1.2"/>
          <circle cx="8.5" cy="5" r="1.2"/>
        </svg>
      </div>
    </NodeViewWrapper>
  );
}

function MentionList({ items, command }) {
  const [selected, setSelected] = useState(0);
  useEffect(() => setSelected(0), [items]);

  return (
    <div className="bg-card border border-border rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto w-60">
      {items.length === 0 && (
        <div className="px-3 py-2 text-xs text-text-secondary">Nenhum usuário</div>
      )}
      {items.map((user, i) => (
        <button
          key={user.id}
          onClick={() => command({ id: String(user.id), label: user.name })}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left ${i === selected ? 'bg-accent/15 text-accent' : 'text-text-primary hover:bg-white/5'}`}
        >
          <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold">
            {user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <span className="truncate">{user.name}</span>
        </button>
      ))}
    </div>
  );
}

function makeSuggestion(users) {
  return {
    items: ({ query }) => {
      const foldedQuery = foldText(query);
      return users
        .filter((u) => foldText(u.name).includes(foldedQuery))
        .slice(0, 6);
    },
    render: () => {
      let component;
      let popup;
      return {
        onStart: (props) => {
          component = new ReactRenderer(MentionList, { props, editor: props.editor });
          if (!props.clientRect) return;
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },
        onUpdate: (props) => {
          component?.updateProps(props);
          if (props.clientRect) popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
        },
        onKeyDown: (props) => {
          if (props.event.key === 'Escape') { popup?.[0]?.hide(); return true; }
          return false;
        },
        onExit: () => { popup?.[0]?.destroy(); component?.destroy(); },
      };
    },
  };
}

const RichEditor = forwardRef(function RichEditor({ value, onChange, users = [], placeholder, autoFocus, onAttachFiles, onFlush }, ref) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-accent underline' } }),
      Placeholder.configure({ placeholder: placeholder || 'Escreva uma atualização e mencione outros com @' }),
      Mention.configure({
        HTMLAttributes: { class: 'text-accent font-medium' },
        renderText: ({ node }) => `@${node.attrs.label}`,
        renderHTML: ({ options, node }) => [
          'span',
          { ...options.HTMLAttributes, 'data-id': node.attrs.id, 'data-label': node.attrs.label },
          `@${node.attrs.label}`,
        ],
        suggestion: makeSuggestion(users),
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: (el) => el.getAttribute('width') ? parseInt(el.getAttribute('width')) : null,
              renderHTML: (attrs) => attrs.width ? { width: attrs.width } : {},
            },
          };
        },
        addNodeView() {
          return ReactNodeViewRenderer(ResizableImageView);
        },
      }).configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'border-collapse border border-border my-2' } }),
      TableRow,
      TableHeader,
      TableCell.configure({ HTMLAttributes: { class: 'border border-border px-2 py-1' } }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose-sm max-w-none focus:outline-none min-h-[60px] px-3 py-2 text-sm text-text-primary',
      },
      transformPastedHTML(html) {
        return html
          .replace(/<!--[\s\S]*?-->/g, '')
          .replace(/<meta[^>]*>/gi, '')
          .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
          .replace(/<o:p>[\s\S]*?<\/o:p>/gi, '')
          .replace(/<\/?o:p>/gi, '')
          .replace(/class="(MsoNormal|Mso[^"]*)"/gi, '');
      },
      handleKeyDown: (view, event) => {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          setTimeout(() => view.dispatch(view.state.tr.scrollIntoView()), 0);
        }
        return false;
      },
      handlePaste: (view, event) => {
        // 1) Imagens (comportamento existente)
        const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
        if (files.length) {
          if (onAttachFiles) {
            onAttachFiles(files);
            return true;
          }
          files.forEach(async (file) => {
            const src = await fileToBase64(file);
            editor.chain().focus().setImage({ src }).run();
          });
          return true;
        }

        // 2) Se há HTML no clipboard (Word, Docs, web), deixa o Tiptap processar (preserva formatação)
        const html = event.clipboardData?.getData('text/html');
        if (html && html.trim()) return false;

        // 3) Plain text: cada \n vira <br> em UM único <p> (espaçamento proporcional ao original)
        const text = event.clipboardData?.getData('text/plain');
        if (text) {
          event.preventDefault();
          const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          const htmlOut = '<p>' + escapeHtml(normalized).replace(/\n/g, '<br>') + '</p>';
          editor.commands.insertContent(htmlOut);
          return true;
        }

        return false;
      },
    },
    autofocus: autoFocus ? 'end' : false,
  });

  useImperativeHandle(ref, () => ({
    clear: () => editor?.commands.setContent(''),
    setContent: (html) => editor?.commands.setContent(html || ''),
    focus: () => editor?.commands.focus('end'),
    mention: () => editor?.chain().focus().insertContent('@').run(),
    getHTML: () => editor?.getHTML() ?? '',
  }));

  // Flush sincrono do conteudo no unmount: garante que fechar o painel
  // (ou trocar de aba) persista o ultimo estado, mesmo antes do debounce.
  // Refs evitam stale closure no cleanup [] e mantem o editor acessivel.
  const editorRefInternal = useRef(editor);
  editorRefInternal.current = editor;
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;
  useEffect(() => {
    return () => {
      const ed = editorRefInternal.current;
      if (ed && onFlushRef.current) onFlushRef.current(ed.getHTML());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) editor.commands.setContent(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btn = (active) =>
    `p-1 rounded transition-colors ${active ? 'bg-accent/20 text-accent' : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'}`;

  const toggleLink = async () => {
    const prev = editor.getAttributes('link').href;
    const url = await dialog.prompt('Inserir link', {
      label: 'URL',
      defaultValue: prev || 'https://',
      placeholder: 'https://',
      confirmLabel: prev ? 'Atualizar' : 'Adicionar',
    });
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="bg-main border border-border rounded-lg focus-within:border-accent/50 transition-colors">
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border/50">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive('bold'))} title="Negrito">
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive('italic'))} title="Itálico">
          <Italic className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btn(editor.isActive('strike'))} title="Riscado">
          <Strikethrough className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-border/50 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive('bulletList'))} title="Lista">
          <List className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive('orderedList'))} title="Lista numerada">
          <ListOrdered className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-border/50 mx-1" />
        <button type="button" onClick={toggleLink} className={btn(editor.isActive('link'))} title="Link">
          <Link2 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().insertContent('@').run()}
          className={btn(false)}
          title="Mencionar"
        >
          <AtSign className="w-3.5 h-3.5" />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default RichEditor;
